import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { queueSummary } from './sync/db';
import { requestPersistentStorage } from './sync/triggers';
import { button, card, color, container, h1, muted, page } from './ui';

/**
 * DEVICE CHECK - /delivery/driver/device. What this handset actually has, read
 * live, no DevTools: every row is a yes/no a person holding the phone can act
 * on. Also a button that asks the SERVICE WORKER (not the page) to drain the
 * upload queue - the same code path Background Sync runs.
 */
interface DeviceCheck {
  secure: boolean;
  standalone: boolean;
  swActive: boolean;
  swScript: string;
  swControlling: boolean;
  backgroundSyncApi: boolean;
  backgroundSyncPermission: string;
  persisted: string;
  usedMb: string;
  quotaMb: string;
  online: boolean;
  pendingStops: number;
}

async function readDeviceCheck(): Promise<DeviceCheck> {
  let swActive = false;
  let swScript = '';
  let backgroundSyncApi = false;
  try {
    const reg = await navigator.serviceWorker?.getRegistration('/');
    swActive = !!reg?.active;
    swScript = reg?.active?.scriptURL ? new URL(reg.active.scriptURL).pathname : '';
    backgroundSyncApi = !!reg && 'sync' in reg && 'SyncManager' in window;
  } catch {
    /* no service worker support */
  }
  let backgroundSyncPermission = 'not reported';
  try {
    const status = await navigator.permissions?.query({ name: 'background-sync' as PermissionName });
    if (status) backgroundSyncPermission = status.state;
  } catch {
    /* this browser does not report it */
  }
  let persisted = 'not supported';
  let usedMb = '?';
  let quotaMb = '?';
  try {
    if (navigator.storage?.persisted) persisted = (await navigator.storage.persisted()) ? 'YES' : 'NO';
    const est = await navigator.storage?.estimate?.();
    if (est) {
      usedMb = ((est.usage ?? 0) / 1e6).toFixed(1);
      quotaMb = ((est.quota ?? 0) / 1e6).toFixed(0);
    }
  } catch {
    /* leave as reported */
  }
  return {
    secure: window.isSecureContext,
    standalone: window.matchMedia('(display-mode: standalone)').matches,
    swActive,
    swScript,
    swControlling: !!navigator.serviceWorker?.controller,
    backgroundSyncApi,
    backgroundSyncPermission,
    persisted,
    usedMb,
    quotaMb,
    online: navigator.onLine,
    pendingStops: (await queueSummary()).pendingStops,
  };
}

export default function DeviceCheckPage() {
  const [check, setCheck] = useState<DeviceCheck | null>(null);
  const [note, setNote] = useState('');
  const load = useCallback(async () => setCheck(await readDeviceCheck()), []);

  useEffect(() => {
    void load();
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'RBF_DRIVER_DRAIN_RESULT') {
        const r = event.data.result;
        setNote(`Service worker drained: ${r.synced} synced, ${r.failed} failed${r.offline ? ', offline' : ''}.`);
        void load();
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [load]);

  const askPersist = async () => {
    const r = await requestPersistentStorage();
    setNote(`Asked Chrome just now: ${r === null ? 'not supported' : r ? 'granted' : 'refused'}`);
    await load();
  };

  const workerDrain = async () => {
    const reg = await navigator.serviceWorker?.getRegistration('/');
    if (!reg?.active) {
      setNote('No active service worker.');
      return;
    }
    setNote('Asked the service worker to upload…');
    reg.active.postMessage({ type: 'RBF_DRIVER_DRAIN' });
  };

  const row = (label: string, ok: boolean | null, value: string) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: `1px solid ${color.greyLight}`, fontSize: 17 }}>
      <span>{label}</span>
      <strong style={{ color: ok === null ? color.ink : ok ? color.green : color.redDark }}>{value}</strong>
    </div>
  );

  return (
    <div style={page}>
      <div style={container}>
        <Link to="/delivery/driver" style={{ ...muted, display: 'inline-block', padding: '12px 0' }}>‹ Route</Link>
        <div style={h1}>Device check</div>
        {!check ? (
          <div style={card}>Reading device…</div>
        ) : (
          <div style={{ ...card, border: `2px solid ${color.ink}` }}>
            {row('Secure page (https)', check.secure, check.secure ? 'YES' : 'NO')}
            {row('Service worker running', check.swActive, check.swActive ? 'YES' : 'NO')}
            {row('Service worker file', check.swScript === '/service-worker.js', check.swScript || '—')}
            {row('Service worker controls this page', check.swControlling, check.swControlling ? 'YES' : 'NO - reload once')}
            {row('Background Sync available', check.backgroundSyncApi, check.backgroundSyncApi ? 'YES' : 'NO')}
            {row('Background Sync permission', check.backgroundSyncPermission === 'granted', check.backgroundSyncPermission)}
            {row('Storage persistent', check.persisted === 'YES', check.persisted)}
            {row('Storage used / available', null, `${check.usedMb} MB / ${check.quotaMb} MB`)}
            {row('Opened as installed app', null, check.standalone ? 'YES' : 'no (browser tab)')}
            {row('Online right now', null, check.online ? 'yes' : 'NO')}
            {row('Stops waiting to upload', check.pendingStops === 0, String(check.pendingStops))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={button(color.white, color.ink)} onClick={load}>Check again</button>
          <button style={button(color.green)} onClick={askPersist}>Ask for persistent storage</button>
          <button style={button(color.blue)} onClick={workerDrain}>Upload via service worker</button>
        </div>
        {note && <div style={{ ...card, marginTop: 12 }}>{note}</div>}
      </div>
    </div>
  );
}
